import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Settings, Loader2, Save, RotateCcw, Eye, Receipt, Printer,
  Monitor, Wallet, Languages, Banknote, CreditCard, Tablet, Smartphone, ExternalLink
} from "lucide-react";
import POSHeader from "./POSHeader";

export default function POSConfigView({ authAxios, onBack }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [config, setConfig] = useState({
    receipt_header: "",
    receipt_footer: "Obrigado pela preferencia!",
    auto_print_receipt: false,
    printer_name: "",
    display_mode: "standard",
    self_service_enabled: false,
    show_product_images: true,
    language: "pt",
    accepted_payments: ["cash", "card", "bizum", "mbway"],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authAxiosRef.current.get("/pos/settings")
      .then(r => { if (r.data && Object.keys(r.data).length > 0) setConfig(prev => ({ ...prev, ...r.data })); })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.put("/pos/settings", config);
      toast.success("Configuracoes salvas!");
    } catch { toast.error("Erro ao salvar"); }
    setSaving(false);
  };

  const togglePayment = (method) => {
    setConfig(prev => ({
      ...prev,
      accepted_payments: prev.accepted_payments.includes(method)
        ? prev.accepted_payments.filter(m => m !== method)
        : [...prev.accepted_payments, method],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const paymentMethods = [
    { id: "cash", label: "Dinheiro", icon: <Banknote className="w-4 h-4" /> },
    { id: "card", label: "Cartao", icon: <CreditCard className="w-4 h-4" /> },
    { id: "bizum", label: "Bizum", icon: <span className="text-[10px] font-black">Biz</span> },
    { id: "mbway", label: "MBWay", icon: <span className="text-[10px] font-black">MB</span> },
  ];

  const displayModes = [
    { id: "standard", label: "Padrao", desc: "Layout normal de POS", icon: <Monitor className="w-5 h-5" /> },
    { id: "tablet", label: "Tablet", desc: "Otimizado para tablets", icon: <Tablet className="w-5 h-5" /> },
    { id: "compact", label: "Compacto", desc: "Para telas menores", icon: <Smartphone className="w-5 h-5" /> },
  ];

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex flex-col" data-testid="pos-config">
      <POSHeader title="Configuracoes do POS" icon={Settings} onBack={onBack}
        rightContent={
          <Button size="sm" className="bg-white/20 hover:bg-white/30 text-white font-bold text-xs gap-1.5" onClick={handleSave} disabled={saving} data-testid="pos-config-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Salvar
          </Button>
        }
      />
      <ScrollArea className="flex-1">
        <div className="max-w-xl mx-auto p-4 space-y-4">

          {/* Quick link to Operacoes settings */}
          <a
            href="/settings?tab=operacoes"
            className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl p-4 hover:bg-emerald-100 transition-colors"
            data-testid="pos-operacoes-link"
          >
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-sm font-bold text-emerald-800">Pagamento na mesa</p>
                <p className="text-xs text-emerald-600">Configurar pagamento antecipado ou pos-consumo</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-emerald-500" />
          </a>

          {/* Display Mode */}
          <div className="bg-white rounded-xl border p-4" data-testid="pos-config-display">
            <h3 className="font-bold text-sm text-gray-800 mb-3 flex items-center gap-2"><Eye className="w-4 h-4 text-emerald-600" /> Modo de Exibicao</h3>
            <div className="grid grid-cols-3 gap-2">
              {displayModes.map(m => (
                <button key={m.id}
                  onClick={() => setConfig(prev => ({ ...prev, display_mode: m.id }))}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${config.display_mode === m.id ? "border-emerald-500 bg-emerald-50" : "border-gray-100 hover:border-gray-200"}`}
                  data-testid={`pos-display-${m.id}`}
                >
                  <div className={`mx-auto mb-1.5 ${config.display_mode === m.id ? "text-emerald-600" : "text-gray-400"}`}>{m.icon}</div>
                  <p className="text-xs font-bold text-gray-800">{m.label}</p>
                  <p className="text-[10px] text-gray-400">{m.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Receipt Settings */}
          <div className="bg-white rounded-xl border p-4" data-testid="pos-config-receipt">
            <h3 className="font-bold text-sm text-gray-800 mb-3 flex items-center gap-2"><Receipt className="w-4 h-4 text-emerald-600" /> Recibo</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500">Cabecalho do Recibo</Label>
                <Input
                  value={config.receipt_header}
                  onChange={e => setConfig(prev => ({ ...prev, receipt_header: e.target.value }))}
                  placeholder="Nome do restaurante, endereco..."
                  className="mt-1"
                  data-testid="pos-receipt-header"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Rodape do Recibo</Label>
                <Input
                  value={config.receipt_footer}
                  onChange={e => setConfig(prev => ({ ...prev, receipt_footer: e.target.value }))}
                  placeholder="Obrigado pela preferencia!"
                  className="mt-1"
                  data-testid="pos-receipt-footer"
                />
              </div>
              <div className="flex items-center justify-between py-1">
                <Label className="text-xs text-gray-700">Imprimir recibo automaticamente</Label>
                <Switch checked={config.auto_print_receipt} onCheckedChange={v => setConfig(prev => ({ ...prev, auto_print_receipt: v }))} data-testid="pos-auto-print" />
              </div>
            </div>
          </div>

          {/* Printer */}
          <div className="bg-white rounded-xl border p-4" data-testid="pos-config-printer">
            <h3 className="font-bold text-sm text-gray-800 mb-3 flex items-center gap-2"><Printer className="w-4 h-4 text-emerald-600" /> Impressora</h3>
            <div>
              <Label className="text-xs text-gray-500">Nome da Impressora</Label>
              <Input
                value={config.printer_name}
                onChange={e => setConfig(prev => ({ ...prev, printer_name: e.target.value }))}
                placeholder="Ex: EPSON TM-T20III"
                className="mt-1"
                data-testid="pos-printer-name"
              />
              <p className="text-[10px] text-gray-400 mt-1">Conecte uma impressora termica via USB ou rede</p>
            </div>
          </div>

          {/* Self-Service Toggle */}
          <div className="bg-white rounded-xl border p-4" data-testid="pos-config-selfservice">
            <h3 className="font-bold text-sm text-gray-800 mb-3 flex items-center gap-2"><Monitor className="w-4 h-4 text-emerald-600" /> Autoatendimento</h3>
            <div className="flex items-center justify-between py-1">
              <div>
                <Label className="text-xs text-gray-700">Ativar modo autoatendimento</Label>
                <p className="text-[10px] text-gray-400">Exibe o menu para clientes em um quiosque</p>
              </div>
              <Switch checked={config.self_service_enabled} onCheckedChange={v => setConfig(prev => ({ ...prev, self_service_enabled: v }))} data-testid="pos-selfservice-toggle" />
            </div>
            <div className="flex items-center justify-between py-1 mt-2">
              <div>
                <Label className="text-xs text-gray-700">Exibir imagens dos produtos</Label>
                <p className="text-[10px] text-gray-400">Mostra fotos no modo autoatendimento</p>
              </div>
              <Switch checked={config.show_product_images} onCheckedChange={v => setConfig(prev => ({ ...prev, show_product_images: v }))} data-testid="pos-show-images" />
            </div>
          </div>

          {/* Payment Methods */}
          <div className="bg-white rounded-xl border p-4" data-testid="pos-config-payments">
            <h3 className="font-bold text-sm text-gray-800 mb-3 flex items-center gap-2"><Wallet className="w-4 h-4 text-emerald-600" /> Metodos de Pagamento</h3>
            <div className="space-y-2">
              {paymentMethods.map(pm => (
                <div key={pm.id} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">{pm.icon}</div>
                    <span className="text-sm text-gray-700 font-medium">{pm.label}</span>
                  </div>
                  <Switch
                    checked={config.accepted_payments.includes(pm.id)}
                    onCheckedChange={() => togglePayment(pm.id)}
                    data-testid={`pos-payment-toggle-${pm.id}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Language */}
          <div className="bg-white rounded-xl border p-4" data-testid="pos-config-lang">
            <h3 className="font-bold text-sm text-gray-800 mb-3 flex items-center gap-2"><Languages className="w-4 h-4 text-emerald-600" /> Idioma do POS</h3>
            <div className="grid grid-cols-3 gap-2">
              {[{ id: "pt", label: "Portugues" }, { id: "es", label: "Espanol" }, { id: "en", label: "English" }].map(l => (
                <button key={l.id}
                  onClick={() => setConfig(prev => ({ ...prev, language: l.id }))}
                  className={`py-2.5 rounded-xl border-2 text-xs font-bold transition-all ${config.language === l.id ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gray-100 text-gray-500 hover:border-gray-200"}`}
                  data-testid={`pos-lang-${l.id}`}
                >{l.label}</button>
              ))}
            </div>
          </div>

          {/* Reset */}
          <button className="w-full py-3 text-center text-xs text-gray-400 hover:text-red-400 flex items-center justify-center gap-1.5 transition-colors" onClick={() => setConfig({
            receipt_header: "", receipt_footer: "Obrigado pela preferencia!", auto_print_receipt: false,
            printer_name: "", display_mode: "standard", self_service_enabled: false, show_product_images: true,
            language: "pt", accepted_payments: ["cash", "card", "bizum", "mbway"],
          })} data-testid="pos-config-reset">
            <RotateCcw className="w-3 h-3" /> Restaurar padrao
          </button>

        </div>
      </ScrollArea>
    </div>
  );
}
